import React, { useCallback, useEffect, useState } from 'react';
import { gerenciaAPI } from '../../services/api';
import { MenuAcciones } from '../common/MenuAcciones';
import { ConfirmModal } from '../common/ConfirmModal';
import { SelectorFecha, fechaLarga } from '../common/SelectorFecha';
import { fechaCorta, diasHasta, nombreMes } from '../../utils/periodos';

// Compartir con gerencia: cada acceso es un enlace /informe/<token> con el que
// un gerente entra sin usuario, elige el mes, ajusta la TRM y descarga el PDF o el Excel.

const enlaceDe = (token) => `${window.location.origin}/informe/${token}`;

const fechaHora = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('es-CO', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const estadoDe = (a) => {
  if (!a.activo) return { clase: 'inactivo', texto: 'Desactivado' };
  if (a.expira && diasHasta(a.expira) < 0) return { clase: 'vencido', texto: 'Vencido' };
  return { clase: 'activo', texto: 'Activo' };
};

// Atajos de vencimiento: meses desde hoy (0 = sin vencimiento).
const PLAZOS = [[0, 'Sin vencimiento'], [1, '1 mes'], [3, '3 meses'], [6, '6 meses'], [12, '1 año']];
const enMeses = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const NuevoAccesoModal = ({ onClose, onCrear }) => {
  const [nombre, setNombre] = useState('');
  const [expira, setExpira] = useState('');
  const [enviando, setEnviando] = useState(false);
  const enviar = async () => {
    setEnviando(true);
    const ok = await onCrear({ nombre: nombre.trim(), expira: expira || null });
    setEnviando(false);
    if (ok) onClose();
  };
  return (
    <div className="modal-overlay" onClick={enviando ? undefined : onClose}>
      <div className="modal-content compartir-nuevo" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-text">
            <span className="compartir-nuevo-eyebrow">Compartir</span>
            <h3 className="modal-title">Nuevo acceso para gerencia</h3>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Cerrar" disabled={enviando}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="modal-body compartir-nuevo-body">
          <div className="cds-campo">
            <label htmlFor="acceso-nombre">Para quién</label>
            <input
              id="acceso-nombre"
              className="cds-input"
              autoFocus
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && nombre.trim() && !enviando) enviar(); }}
              placeholder="Ej. Gerente I+D"
              maxLength={80}
            />
          </div>
          <div className="cds-campo">
            <span className="cds-campo-label">Vencimiento</span>
            <div className="compartir-plazos" role="radiogroup" aria-label="Vencimiento">
              {PLAZOS.map(([n, txt]) => {
                const activo = n === 0 ? !expira : expira === enMeses(n);
                return (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={activo}
                    className={`compartir-plazo ${activo ? 'activo' : ''}`}
                    onClick={() => setExpira(n === 0 ? '' : enMeses(n))}
                  >
                    {txt}
                  </button>
                );
              })}
            </div>
            <SelectorFecha value={expira} onChange={setExpira} placeholder="O elige una fecha" minHoy />
            <small className="cds-ayuda">
              {expira ? `El enlace deja de funcionar el ${fechaLarga(expira)}.` : 'El enlace funciona hasta que lo desactives.'}
            </small>
          </div>
        </div>
        <div className="modal-footer">
          <button className="modal-btn modal-btn-ghost" onClick={onClose} disabled={enviando}>Cancelar</button>
          <button className="modal-btn modal-btn-primary" disabled={!nombre.trim() || enviando} onClick={enviar}>
            {enviando ? 'Creando…' : 'Crear enlace'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Uso de un enlace: cuántas veces entró y qué informes descargó (mes y TRM).
const HistorialModal = ({ acceso, onClose }) => {
  const [eventos, setEventos] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    gerenciaAPI.historial(acceso.id)
      .then((res) => setEventos(res.data.eventos || []))
      .catch(() => setError(true));
  }, [acceso.id]);
  const descargas = (eventos || []).filter((e) => e.tipo === 'descarga');
  const ingresos = (eventos || []).filter((e) => e.tipo === 'ingreso');
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content compartir-historial" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-text">
            <h3 className="modal-title">Uso del enlace · {acceso.nombre}</h3>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Cerrar">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="modal-body">
          {error ? (
            <p className="proceso-hint">No se pudo cargar el historial.</p>
          ) : !eventos ? (
            <p className="proceso-hint">Cargando…</p>
          ) : (
            <>
              <div className="compartir-kpis">
                <div><span>Ingresos</span><strong>{ingresos.length}</strong></div>
                <div><span>Informes descargados</span><strong>{descargas.length}</strong></div>
                <div><span>Última descarga</span><strong className="peq">{descargas[0] ? fechaHora(descargas[0].created_at) : '—'}</strong></div>
              </div>
              <h4 className="compartir-historial-titulo">Informes descargados</h4>
              {descargas.length === 0 ? (
                <p className="proceso-hint">Todavía no ha descargado ningún informe.</p>
              ) : (
                <div className="compartir-historial-lista">
                  <table className="consumo-tabla">
                    <thead>
                      <tr><th>Fecha y hora</th><th>Informe del mes</th><th>Formato</th><th className="num">TRM usada</th></tr>
                    </thead>
                    <tbody>
                      {descargas.map((e, i) => (
                        <tr key={i}>
                          <td>{fechaHora(e.created_at)}</td>
                          <td><strong>{e.periodo ? nombreMes(e.periodo) : '—'}</strong></td>
                          <td>{e.formato === 'excel' ? 'Excel' : 'PDF'}</td>
                          <td className="num">{e.trm ? `$${e.trm.toLocaleString('es-CO')}` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="modal-btn modal-btn-primary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
};

export const CompartirModule = ({ showNotification }) => {
  const [accesos, setAccesos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creando, setCreando] = useState(false);
  const [eliminar, setEliminar] = useState(null);
  const [historial, setHistorial] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const res = await gerenciaAPI.getAccesos();
      setAccesos(res.data.accesos || []);
    } catch {
      showNotification?.('error', 'Compartir', 'No se pudieron cargar los accesos');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { cargar(); }, [cargar]);

  const copiar = async (a) => {
    try {
      await navigator.clipboard.writeText(enlaceDe(a.token));
      showNotification?.('success', 'Enlace copiado', `Compártelo con ${a.nombre}`);
    } catch {
      showNotification?.('error', 'No se pudo copiar', enlaceDe(a.token));
    }
  };

  const crear = async (datos) => {
    try {
      const res = await gerenciaAPI.crear(datos);
      await cargar();
      copiar(res.data.acceso);
      return true;
    } catch (e) {
      showNotification?.('error', 'No se pudo crear', e.response?.data?.error || 'Intenta de nuevo');
      return false;
    }
  };

  const editar = async (a, datos, mensaje) => {
    try {
      await gerenciaAPI.editar(a.id, datos);
      await cargar();
      showNotification?.('success', 'Acceso actualizado', mensaje);
    } catch (e) {
      showNotification?.('error', 'No se pudo guardar', e.response?.data?.error || 'Intenta de nuevo');
    }
  };

  const confirmarEliminar = async () => {
    const a = eliminar;
    setEliminar(null);
    try {
      await gerenciaAPI.eliminar(a.id);
      await cargar();
      showNotification?.('success', 'Acceso eliminado', `El enlace de ${a.nombre} ya no funciona`);
    } catch {
      showNotification?.('error', 'No se pudo eliminar', 'Intenta de nuevo');
    }
  };

  return (
    <div className="module-proceso">
      <div className="module-header">
        <h1>Compartir</h1>
        <p>Enlaces para que los gerentes elijan el mes, ajusten la TRM y descarguen el informe en PDF o Excel, sin usuario ni contraseña</p>
      </div>

      <div className="consumo-toolbar compartir-toolbar">
        <span className="proceso-hint">
          {accesos.length ? `${accesos.length} enlace${accesos.length === 1 ? '' : 's'}` : ''}
        </span>
        <button type="button" className="btn-primary consumo-cargar-btn" onClick={() => setCreando(true)}>
          Nuevo acceso
        </button>
      </div>

      {loading ? (
        <p className="proceso-hint">Cargando…</p>
      ) : accesos.length === 0 ? (
        <div className="compartir-vacio">
          <h3>Aún no hay enlaces</h3>
          <p>Crea un acceso para un gerente: recibe un enlace con el que descarga el informe del mes que necesite.</p>
          <button type="button" className="btn-primary consumo-cargar-btn" onClick={() => setCreando(true)}>
            Nuevo acceso
          </button>
        </div>
      ) : (
        <div className="consumo-tabla-wrap compartir-wrap">
          <table className="consumo-tabla compartir-tabla">
            <thead>
              <tr>
                <th>Para quién</th>
                <th>Enlace</th>
                <th>Estado</th>
                <th>Vence</th>
                <th>Último ingreso</th>
                <th>Última descarga</th>
                <th className="num">Descargas</th>
                <th aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {accesos.map((a) => {
                const est = estadoDe(a);
                return (
                  <tr key={a.id} className="compartir-fila" onClick={() => setHistorial(a)} title="Ver uso del enlace">
                    <td><strong>{a.nombre}</strong></td>
                    <td>
                      <button type="button" className="compartir-copiar" onClick={(e) => { e.stopPropagation(); copiar(a); }} title={enlaceDe(a.token)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <rect x="9" y="9" width="13" height="13" rx="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        Copiar enlace
                      </button>
                    </td>
                    <td><span className={`compartir-estado ${est.clase}`}>{est.texto}</span></td>
                    <td>{a.expira ? fechaCorta(a.expira) : 'Sin vencimiento'}</td>
                    <td>{fechaHora(a.ultimo_acceso)}</td>
                    <td>{a.ultima_descarga ? <strong>{fechaHora(a.ultima_descarga)}</strong> : <span className="compartir-pendiente">Sin descargas</span>}</td>
                    <td className="num">{a.descargas}</td>
                    <td className="compartir-acciones" onClick={(e) => e.stopPropagation()}>
                      <MenuAcciones
                        label={`Acciones de ${a.nombre}`}
                        acciones={[
                          { label: 'Ver uso del enlace', onClick: () => setHistorial(a) },
                          { label: 'Copiar enlace', onClick: () => copiar(a) },
                          { label: 'Abrir como gerente', onClick: () => window.open(enlaceDe(a.token), '_blank', 'noopener') },
                          a.activo
                            ? { label: 'Desactivar', onClick: () => editar(a, { activo: false }, `El enlace de ${a.nombre} quedó desactivado`) }
                            : { label: 'Activar', onClick: () => editar(a, { activo: true }, `El enlace de ${a.nombre} vuelve a funcionar`) },
                          { label: 'Reiniciar seguimiento', onClick: () => editar(a, { reiniciarDescargas: true }, `Seguimiento de ${a.nombre} en cero: verás aquí su próxima descarga`) },
                          ...(a.expira ? [{ label: 'Quitar vencimiento', onClick: () => editar(a, { expira: null }, 'El enlace ya no vence') }] : []),
                          { label: 'Eliminar', danger: true, onClick: () => setEliminar(a) }
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {historial && <HistorialModal acceso={historial} onClose={() => setHistorial(null)} />}
      {creando && <NuevoAccesoModal onClose={() => setCreando(false)} onCrear={crear} />}
      {eliminar && (
        <ConfirmModal
          title="Eliminar acceso"
          message={`El enlace de ${eliminar.nombre} dejará de funcionar. Esta acción no se puede deshacer.`}
          onConfirm={confirmarEliminar}
          onClose={() => setEliminar(null)}
        />
      )}
    </div>
  );
};
